import type { IObjective } from "@bbj-types/challenge";
import { StackGrid, stackGridClasses } from "@data-display/layout/StackGrid";
import { AutocompleteForm, DateInputForm, TextFieldForm } from "@inputs/hook-form";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { Control } from "react-hook-form";
import type { TPaperCandidateChallengeObjective } from "../types";
import type { IFormEditCandidateChallenge, IResolverEditCandidateChallenge } from "./logics";

export interface IPaperCandidateChallengeEditObjectiveProps<
  CandidateObjective extends TPaperCandidateChallengeObjective,
> {
  control: Control<
    IFormEditCandidateChallenge<CandidateObjective>,
    void,
    IResolverEditCandidateChallenge<CandidateObjective>
  >;
  index: number;
  source?: CandidateObjective;
  objectives: IObjective[];
  disabled?: boolean;
  onRemove: () => void;
}

export const PaperCandidateChallengeEditObjective = <CandidateObjective extends TPaperCandidateChallengeObjective>({
  control,
  index,
  source,
  objectives,
  disabled,
  onRemove,
}: IPaperCandidateChallengeEditObjectiveProps<CandidateObjective>) => {
  const locked = Boolean(source?.createdFromCer);
  return (
    <Stack sx={{ gap: 1 }}>
      <Typography variant="subtitle2SemiBold" color="primary">
        Objectif #{index + 1}
      </Typography>
      <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}>
        <StackGrid perLine={{ tablet: 2 }} gap={2} sx={{ flex: "auto" }}>
          <AutocompleteForm
            control={control}
            name={`objectives.${index}.objective`}
            options={objectives}
            getOptionKey={(option) => option._id}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            label="Type d'objectif *"
            size="small"
            disabled={Boolean(source?._id)}
            fullWidth
          />
          <DateInputForm
            control={control}
            name={`objectives.${index}.objectiveEndDate`}
            label="Date butoir"
            TextFieldProps={{ size: "small", fullWidth: true }}
            disablePast
          />
          <TextFieldForm
            control={control}
            name={`objectives.${index}.objectiveTitle`}
            maxLength={80}
            label="Titre"
            size="small"
            fullWidth
            className={stackGridClasses.fullWidth}
          />
        </StackGrid>
        <Tooltip
          title={locked ? "Vous ne pouvez pas supprimer cet objectif car il est lié au CE en cours." : undefined}
        >
          <span>
            <IconButton color="primary" onClick={onRemove} disabled={disabled || locked}>
              {locked ? <LockIcon /> : <DeleteIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
